import { computed, ref, watch, toValue } from "vue";
import type { MaybeRefOrGetter } from "vue";
import { useQuery } from "@urql/vue";
import type {
  ResultOf,
  TypedDocumentNode,
} from "@graphql-typed-document-node/core";
import { graphQlClient } from "@/utils/urql";

export function useInfiniteQuery<Q extends TypedDocumentNode<any, any>, T>({
  query,
  variables,
  pause,
  onData,
  extractData,
  dedupeKey,
  limit = 10,
}: {
  query: Q;
  variables: MaybeRefOrGetter<ResultOf<Q>["variables"]>;
  pause?: MaybeRefOrGetter<boolean>;
  onData?: (data: T[]) => void;
  extractData?: (result: ResultOf<Q>) => T[];
  /**
   * Optional stable identity for an item. When provided, the accumulated list
   * is de-duplicated by this key on every merge — later occurrences are
   * dropped. Use for paginated lists whose pages can briefly overlap across
   * separate refetch requests (e.g. an item shifting across a page boundary
   * when the underlying list changes between two `refetchAllPages` fetches),
   * so a keyed virtual list never receives two rows with the same id.
   */
  dedupeKey?: (item: T) => string;
  limit: number;
}) {
  const page = ref(0);
  const allItems = ref<T[]>([]);
  const hasInitialLoad = ref(false);
  const hasMore = ref(true);

  function dedupe(items: T[]): T[] {
    if (!dedupeKey) return items;
    const seen = new Set<string>();
    const out: T[] = [];
    for (const item of items) {
      const key = dedupeKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  }

  const {
    data: queryResult,
    fetching: loading,
    executeQuery: refetch,
  } = useQuery({
    query: query,
    variables: computed(() => ({
      ...toValue(variables),
      page: page.value,
      limit: limit,
    })),
    pause,
    requestPolicy: "network-only",
  });

  // Reset when non-pagination variables change
  watch(
    () => toValue(variables),
    () => {
      reset();
      refetch();
    },
    { deep: true },
  );

  watch(queryResult, (newResult) => {
    if (!newResult) {
      hasMore.value = false;
      return;
    }

    const newItems = extractData ? extractData(newResult) : (newResult as T[]);
    if (page.value === 0) {
      allItems.value = dedupe(newItems);
    } else {
      allItems.value = dedupe([...allItems.value, ...newItems] as T[]);
    }

    hasMore.value = newItems.length > 0;

    if (onData) {
      onData(allItems.value as T[]);
    }
  });

  async function refetchAllPages() {
    const fresh: T[] = [];
    let freshHasMore = true;

    for (let i = 0; i <= page.value; i++) {
      const { data: _data, error } = await graphQlClient.value.query(
        query,
        { ...toValue(variables), page: i, limit: limit },
        { requestPolicy: "network-only" },
      );
      const data = toValue(_data);

      if (!data) {
        console.error("Failed to fetch data for page", i, error);
        return;
      }

      const items = extractData ? extractData(data) : (data as T[]);
      if (items.length === 0) {
        freshHasMore = false;
        break;
      }

      fresh.push(...items);
    }

    allItems.value = dedupe(fresh);
    hasMore.value = freshHasMore;
  }

  function loadMore() {
    if (!loading.value && hasMore.value) {
      page.value++;
      hasInitialLoad.value = true;
    }
  }

  function reset() {
    page.value = 0;
    allItems.value = [];
    hasInitialLoad.value = false;
    hasMore.value = true;
  }

  return {
    items: computed(() => allItems.value),
    loading,
    loadMore,
    reset,
    hasInitialLoad,
    refetch: refetchAllPages,
    hasMore,
  };
}
